import React from 'react';
import { FiCheckCircle, FiArrowRight, FiTwitter, FiMessageSquare, FiHash, FiTrendingUp, FiUsers, FiAward } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';

const TwitterMarketingPage = () => {
  const features = [
    {
      icon: FiTwitter,
      title: "Content Creation",
      description: "We write tweets and threads that people actually want to read, share, and engage with—no fluff, just value"
    },
    {
      icon: FiMessageSquare,
      title: "Trend Monitoring & Engagement",
      description: "We keep your brand part of the conversation by jumping on trends and responding to your audience in real time"
    },
    {
      icon: FiHash,
      title: "Twitter Ads & Promotion",
      description: "We run targeted Twitter ads that put your brand in front of the right people at the right time"
    },
    {
      icon: FiTrendingUp,
      title: "Community Building & Growth",
      description: "We grow your follower base with real people who care about your brand—not fake bots or empty numbers"
    }
  ];

  const pricingPlans = [
    {
      name: "Basic",
      price: "₹12,000",
      period: "/month",
      description: "Perfect for businesses starting on Twitter/X",
      features: [
        "15 Tweets per Month",
        "Content Calendar",
        "Hashtag Research",
        "Basic Engagement",
        "Monthly Analytics Report",
        "Email Support"
      ],
      highlighted: false
    },
    {
      name: "Professional",
      price: "₹25,000",
      period: "/month",
      description: "Ideal for growing your Twitter presence and engagement",
      features: [
        "30 Tweets per Month",
        "5 Twitter Threads",
        "Trending Topic Monitoring",
        "Twitter Ads Management",
        "Daily Engagement",
        "Bi-weekly Performance Reports",
        "Phone + Email Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For brands needing full Twitter marketing management",
      features: [
        "Unlimited Tweets",
        "Unlimited Threads",
        "Real-time Engagement",
        "Advanced Ad Campaigns",
        "Crisis Management",
        "Influencer Collaboration",
        "Weekly Strategy Calls",
        "Dedicated Account Manager"
      ],
      highlighted: false
    }
  ];

  const whyChooseUs = [
    {
      title: "We post content that actually gets seen",
      description: "No boring corporate speak—just tweets and threads that catch attention and get engagement"
    },
    {
      title: "We're always on—so you don't have to be",
      description: "We monitor trends 24/7 and keep your brand active, engaged, and part of the conversation"
    },
    {
      title: "Real followers, real engagement, real results",
      description: "We grow your Twitter/X presence with genuine people who care about what you offer, not bots or fake followers"
    }
  ];

  const faqs = [
    {
      question: "How often should we tweet for optimal engagement?",
      answer: "Optimal posting frequency on Twitter is 3-5 times daily for consistent visibility. Our Professional plan includes 30 tweets monthly (1 per day) plus threads. More important than frequency is timing—we schedule tweets when your audience is most active based on analytics."
    },
    {
      question: "What makes Twitter different from other social platforms?",
      answer: "Twitter (X) is a real-time, conversation-focused platform where news breaks first and discussions happen instantly. It's ideal for thought leadership, customer service, trend participation, and direct engagement with audience. The 280-character limit demands concise, impactful messaging that drives high engagement rates."
    },
    {
      question: "How do Twitter threads work and why are they effective?",
      answer: "Twitter threads are series of connected tweets that tell longer stories or share detailed insights. They perform exceptionally well because Twitter's algorithm promotes threads, they're easy to read/share, and they position you as a thought leader. We create compelling threads that educate, entertain, and drive engagement."
    },
    {
      question: "Should we focus on hashtags or is that outdated?",
      answer: "Hashtags are still relevant but should be used strategically—1-2 relevant hashtags per tweet is optimal. We research trending and niche hashtags in your industry, create branded hashtags, and participate in popular hashtag conversations to increase discoverability without looking spammy."
    },
    {
      question: "How much do Twitter Ads cost and are they worth it?",
      answer: "Twitter ads typically cost ₹30-₹100 per engagement or ₹300-₹800 per follower depending on targeting. Promoted tweets can significantly amplify organic reach. We recommend starting with ₹25,000-₹40,000 monthly ad spend to test campaigns, then scale winners. Twitter ads are particularly effective for product launches and event promotion."
    },
    {
      question: "Can you help with crisis management on Twitter?",
      answer: "Absolutely! Twitter is often where PR crises emerge. Our Enterprise plan includes crisis monitoring and management—we track brand mentions 24/7, respond quickly to negative tweets, escalate issues appropriately, and execute crisis communication plans to protect your reputation."
    },
    {
      question: "What's your approach to engagement and community management?",
      answer: "We actively engage with your audience daily—replying to mentions, answering questions, liking relevant tweets, and joining industry conversations. This humanizes your brand, builds relationships, and increases visibility. Real engagement (not just broadcasting) is key to Twitter success."
    },
    {
      question: "How do you measure Twitter marketing success?",
      answer: "We track multiple metrics: follower growth rate, engagement rate (likes, retweets, replies), impressions, profile visits, link clicks, and conversions from Twitter traffic. More importantly, we measure quality—are we attracting your target audience and driving business outcomes, not just vanity metrics."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = React.useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#000000] to-[#14171A] text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Twitter / X Marketing
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              Twitter/X Marketing helps you build real connections, join trending conversations, and grow your brand where people are talking
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#pricing"
                className="bg-white text-[#000000] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                View Pricing
              </a>
              <a
                href="#contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#000000] transition-colors inline-block"
              >
                Get Free Audit
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">
              What is Twitter / X Marketing?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                Twitter (now called X) marketing is a simple and effective way to reach your ideal audience, join trending conversations, and build a community around your brand. People come to twitter marketing to share ideas, follow trends, and engage with brands they care about. As an X marketing agency, we help you show up in the right conversations, post content that gets noticed, and run ads that drive real results—whether that's followers, website visits, or sales.
              </p>
              <p className="text-lg leading-relaxed">
                You don't need to be a marketing expert to succeed on Twitter/X. We handle everything—from writing tweets and creating threads to running targeted ads and engaging with your audience. We focus on what works: real tweets, timely posts, and smart targeting that turns casual scrollers into loyal customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our Twitter Marketing Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-[#000000] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 text-[#000000]">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include expert management and optimization.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-8 ${
                  plan.highlighted
                    ? 'bg-[#000000] text-white shadow-2xl scale-105 border-4 border-[#000000]'
                    : 'bg-white border-2 border-gray-200 shadow-lg'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-white text-[#000000] px-4 py-1 rounded-full text-sm font-semibold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#000000]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-lg ${
                      plan.highlighted ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#000000]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-gray-300' : 'text-gray-700'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-white text-[#000000] hover:bg-gray-100'
                      : 'bg-[#000000] text-white hover:bg-[#14171A]'
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Choose Mirani Marketing for Twitter?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#000000] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#000000]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-[#000000] text-2xl">
                    {openFaqIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-[#000000] to-[#14171A] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Build Your Twitter Presence?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-300">
            Get a free Twitter audit and discover how to grow your audience and engagement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-[#000000] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get Free Audit
            </a>
            <a
              href="tel:+919876543210"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#000000] transition-colors inline-block"
            >
              Call: +91 98765 43210
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TwitterMarketingPage;

